/* eslint no-shadow: ['error', { 'allow': ['state', 'getters'] }] */
import axios from 'axios';
import { fcoUrl } from '@/fcoModules/utilities';
import { VEHICLE_ATTRIBUTE_IDS } from '@/common/constants/vehicle';
import { createRequestStatus, createAxiosResponse, trackRequestStatus, updateRequestStatus } from '../request-status';
import { CacheStrategy, StorageKey, getStorageItem, removeStorageItem, setStorageItem } from '../store-utils';
import * as attributeService from '../../services/attribute-service';

export const state = {
    forceUrl: null,
    saveLookupBeforeRedirect: false,
    partsToSaveBeforeRedirect: null,
    partTypePendingForceCar: false,
    height: 0,
    isLoadingData: false,
    isOpen: false,
    isOpenDefault: false,
    isRecentVehiclesModalShown: false,
    isRecentVehiclesDataLoaded: false,
    isVehicleInfoShown: false,
    isYearSelectorOpen: false,
    isEditSelectionOpen: false,
    recentVehicles: [],
    errorLoadingData: false,
    preventNavigationOnVehicleUpdate: false,

    // When true, PLP/PDP "vehicle required" watchers should NOT auto-redirect to /catalog/browse.html
    // if the current vehicle is cleared. Used during masquerade start/stop so the intended
    // masqueradeSource / landing-page redirect is not hijacked by the vehicle-loss watchers.
    suppressVehicleRequiredRedirect: false,

    currentVehicle: getStorageItem(StorageKey.CURRENT_VEHICLE),
    vsData: {
        states: [],
        years: [],
    },
    requests: {
        getCurrentVehicle: createRequestStatus({
            storageKey: StorageKey.CURRENT_VEHICLE,
            cacheStrategy: CacheStrategy.LAZY,
            pollKey: 'vehicle',
            pollValue: ({ state }) => state.vehicleSelector.currentVehicle?.id,
        }),
        getVehicleSelectorData: createRequestStatus(),
        getAnsweredAttributesTranslated: createRequestStatus(),
    },
};

export const mutations = {
    updateRequestStatus,
    setRecentVehicles(state, recentVehicles) {
        state.recentVehicles = recentVehicles;
    },
    setIsRecentVehiclesModalShown(state, value) {
        state.isRecentVehiclesModalShown = value;
    },
    setIsVehicleInfoShown(state, value) {
        state.isVehicleInfoShown = value;
    },
    setCurrentVehicle(state, currentVehicle) {
        // some components commit to this mutation directly so need to be safe against invalid vehicles being saved
        state.currentVehicle = currentVehicle?.id && currentVehicle?.vehicleId ? currentVehicle : null;

        // required for FE data persistence
        setStorageItem(StorageKey.CURRENT_VEHICLE, state.currentVehicle);
    },
    setAttributes(state, attributes) {
        state.currentVehicle.answeredAttributes = attributes;
        setStorageItem(StorageKey.CURRENT_VEHICLE, state.currentVehicle);
    },
    setVehicleSelectorData(state, data) {
        state.vsData = data;
    },
    resetVehicleData(state) {
        state.currentVehicle = null;

        // required for FE data persistence
        setStorageItem(StorageKey.CURRENT_VEHICLE, null);
        removeStorageItem(StorageKey.PTV_LAST_STATE);
    },
    addAttribute(state, { attributeId, attributeDescription, valueId, valueDescription, displayDescription }) {
        const index = state.currentVehicle.answeredAttributes.findIndex((attribute) => attribute.attributeId === attributeId);

        // if vehicle already has this attribute type then replace it, otherwise just add it.
        if (index !== -1) {
            state.currentVehicle.answeredAttributes.splice(index, 1, {
                attributeId,
                attributeDescription,
                valueId,
                valueDescription,
                displayDescription,
            });
        } else {
            state.currentVehicle.answeredAttributes.push({ attributeId, attributeDescription, valueId, valueDescription, displayDescription });
        }

        // required for FE data persistence
        setStorageItem(StorageKey.CURRENT_VEHICLE, state.currentVehicle);
    },
    removeAttributes(state, ids) {
        const { answeredAttributes } = state.currentVehicle;
        state.currentVehicle.answeredAttributes = answeredAttributes.filter((attr) => !ids.includes(Number(attr.attributeId)));

        // required for FE data persistence
        setStorageItem(StorageKey.CURRENT_VEHICLE, state.currentVehicle);
    },
    setVsProp(state, { prop, value }) {
        state[prop] = value;
    },
    setShopCustomer(state, { firstName, lastName, id }) {
        state.currentVehicle.shopCustomerId = id;
        state.currentVehicle.shopCustomerFirst = firstName;
        state.currentVehicle.shopCustomerLast = lastName;
    },
};

export const actions = {
    async getCurrentVehicle({ commit, dispatch, rootGetters }) {
        const { data: currentVehicle } = await trackRequestStatus(
            commit,
            'getCurrentVehicle',
            (async () => {
                await dispatch('requestIfIdle', ['getFeatures', 'getFeaturesForStore'], { root: true });
                if (rootGetters.isFeatureDisabled('entsearch')) {
                    return axios.get(fcoUrl('/current/vehicle'));
                }
                const cachedVehicleId = state.currentVehicle?.id;
                if (!cachedVehicleId) {
                    return createAxiosResponse();
                }
                return axios.get(fcoUrl(`/vehicle/select/vehicleByWorksheetVehicleId?worksheetVehicleId=${cachedVehicleId}`));
            })()
        );

        commit('setCurrentVehicle', currentVehicle?.id ? currentVehicle : null);
    },
    async getVehicleSelectorData({ commit }) {
        const { data: vsData } = await trackRequestStatus(commit, 'getVehicleSelectorData', axios.get(fcoUrl('/vehicle/select/vehicleSelectorData')));
        commit('setVehicleSelectorData', vsData);
    },
    async getAnsweredAttributesTranslated({ commit }, { worksheetId, worksheetVehicleId }) {
        const { data: vehicleTranslated } = await trackRequestStatus(
            commit,
            'getAnsweredAttributesTranslated',
            axios.get(fcoUrl(`/worksheet/rest/${worksheetId}/vehicle/${worksheetVehicleId}/translate`))
        );
        commit('setAttributes', vehicleTranslated.answeredAttributes);
    },
    async clearCurrentVehicle({ state, commit, dispatch }) {
        commit('resetVehicleData');
        dispatch('usageTracking/clearLookups', null, { root: true });
        if (!state.isOpenDefault) {
            commit('setVsProp', { prop: 'isOpen', value: false });
        }
    },
    async getRecentVehicles({ commit }, shopId, isEntSearchEnabled) {
        const pathSegment = isEntSearchEnabled ? 'enterprise' : 'v2';
        const { data: recentVehicles } = await axios.get(fcoUrl(`/recent/vehicles/${pathSegment}`), { params: { shopId } });
        commit('setRecentVehicles', recentVehicles);
        commit('setVsProp', { prop: 'isRecentVehiclesDataLoaded', value: true });
    },
    async deleteRecentVehicle({ commit, state }, vehicleId) {
        const url = fcoUrl(`/recent/deleteRecentVehicle.json?recentVehicleId=${vehicleId}`);

        await axios.delete(url);
        const recentVehicles = state.recentVehicles.filter((vehicle) => vehicle.id !== vehicleId);

        commit('setRecentVehicles', recentVehicles);
    },
    async selectEngineSubmodel({ state, commit, dispatch, rootGetters }, { engineId, submodelId }) {
        await dispatch('requestIfIdle', ['getFeatures', 'getFeaturesForStore'], { root: true });
        const isEntSearchDisabled = rootGetters.isFeatureDisabled('entsearch');
        const url = isEntSearchDisabled
            ? fcoUrl('/vehicle/select/v2/selectAttributes')
            : fcoUrl(`/vehicle/select/setEngineSubmodel?worksheetVehicleId=${state.currentVehicle?.id}`);

        const { data } = await axios.post(url, { engineId, submodelId });

        commit('setCurrentVehicle', isEntSearchDisabled ? data.vehicle : data);
        dispatch('usageTracking/clearLookups', null, { root: true });

        if (!state.isOpenDefault) {
            commit('setVsProp', { prop: 'isOpen', value: false });
        }
    },
    async removeRetainAttributes({ state, commit, dispatch, rootGetters }, attributeIds) {
        await dispatch('requestIfIdle', ['getFeatures', 'getFeaturesForStore'], { root: true });

        if (rootGetters.isFeatureDisabled('entsearch')) {
            // The legacy endpoint to clear attributes seems to require synchronous requests to clear attributes correctly.
            for await (const attributeId of attributeIds) {
                await attributeService.remove({ attributeId, updateAttributeQuestion: true });
            }
        } else {
            await attributeService.removeRetainAttributes(attributeIds, state.currentVehicle?.id);
        }

        commit('removeAttributes', attributeIds);
    },
    setVsProp({ commit }, data) {
        commit('setVsProp', data);
    },
    autoExpand({ commit }, value = true) {
        if (value) {
            commit('setVsProp', { prop: 'isOpen', value });
        }
        commit('setVsProp', { prop: 'isOpenDefault', value });
    },
    openYearSelect({ commit }) {
        commit('setVsProp', { prop: 'isOpen', value: true });
        commit('setVsProp', { prop: 'isYearSelectorOpen', value: true });
    },
    openEditOptions({ commit }) {
        commit('setVsProp', { prop: 'isEditSelectionOpen', value: true });
    },
    forceCar({ commit, state }, newUrl = state.isSPA ? window.location.pathname + window.location.search : window.location.href) {
        commit('setVsProp', { prop: 'forceUrl', value: newUrl });
        commit('setVsProp', { prop: 'isOpen', value: true });
    },
};

export const getters = {
    shopCustomer: ({ currentVehicle }) => {
        if (!currentVehicle?.shopCustomerId) return null;
        return {
            id: currentVehicle.shopCustomerId,
            firstName: currentVehicle.shopCustomerFirst,
            lastName: currentVehicle.shopCustomerLast,
        };
    },
    vehicleEngine: ({ currentVehicle }) => currentVehicle.answeredAttributes.find((attr) => attr.attributeId === VEHICLE_ATTRIBUTE_IDS.ENGINE),
    vehicleSubmodel: ({ currentVehicle }) => currentVehicle.answeredAttributes.find((attr) => attr.attributeId === VEHICLE_ATTRIBUTE_IDS.SUBMODEL),
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
